import { useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

/** Доля ширины строки, после которой действие срабатывает. */
const THRESHOLD = 0.4;

/**
 * Зона у края экрана, где жест начинать нельзя.
 *
 * На iOS свайп от левого края — системное «назад», на Android — то же самое.
 * Жест, начатый там, до строки не доедет: система заберёт его себе.
 */
const EDGE = 28;

/** Через сколько удержание считается долгим нажатием. */
const LONG_PRESS_MS = 500;

type Action = { icon: IconName; label: string; onAction: () => void };

/**
 * Строка с быстрыми действиями по свайпу — порт `AppSwipeRow` ([D-10], [D-34]).
 *
 * Правила, без которых компонент не имеет смысла:
 *  - **порог, а не автосрабатывание.** Действие срабатывает после 40 % ширины; полный
 *    свайп ничего не запускает сам — на ходу и в кармане это ловит ложные жесты;
 *  - **жест никогда не единственный путь** (WCAG 2.2, SC 2.5.7): те же действия обязаны
 *    быть в меню долгого нажатия, поэтому [onLongPress] здесь не «дополнительно», а часть
 *    договора;
 *  - **свайп ничего не удаляет.** Строка срабатывает и возвращается на место.
 *
 * Вибрации на пороге, как на Android, не будет: `navigator.vibrate` в Safari
 * не работает. Порог показывает только подложка — docs/PITFALLS.md §17.
 */
export function AppSwipeRow({
  start,
  end,
  onLongPress,
  children,
}: {
  start?: Action;
  end?: Action;
  onLongPress?: () => void;
  children: ReactNode;
}) {
  const [offset, setOffset] = useState(0);
  /*
   * Смещение живёт и в ссылке: порог проверяется в момент отпускания, а состояние
   * к этому моменту может ещё не отрисоваться — быстрый свайп успевает закончиться
   * раньше перерисовки, и действие не срабатывает. Поймано на переходах экранов
   * 04.09.2026, здесь ошибка была ровно та же.
   */
  const travelled = useRef(0);
  const box = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ x: number; y: number; horizontal: boolean | null; timer?: number }>({
    x: 0,
    y: 0,
    horizontal: null,
  });

  const width = () => box.current?.getBoundingClientRect().width ?? 1;

  const stopTimer = () => {
    if (gesture.current.timer) window.clearTimeout(gesture.current.timer);
    gesture.current.timer = undefined;
  };

  const onPointerDown = (event: React.PointerEvent) => {
    /*
     * Жест начинает только основное касание.
     *
     * Правая кнопка мыши и второй палец до строки не доводят: за ними идёт контекстное
     * меню, а `pointerup` может не прийти вовсе — и таймер долгого нажатия срабатывал
     * уже после того, как меню отработало. На библиотеке документов это выглядело так:
     * пункт «Выбрать несколько» включал выделение, а через полсекунды оно само гасло.
     */
    if (!event.isPrimary || event.button !== 0) return;

    const rect = box.current?.getBoundingClientRect();
    if (rect && (event.clientX - rect.left < EDGE || rect.right - event.clientX < EDGE)) return;

    gesture.current = { x: event.clientX, y: event.clientY, horizontal: null };
    travelled.current = 0;
    if (onLongPress) {
      gesture.current.timer = window.setTimeout(() => {
        if (gesture.current.horizontal !== true) onLongPress();
      }, LONG_PRESS_MS);
    }
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (gesture.current.horizontal === false) return;

    const dx = event.clientX - gesture.current.x;
    const dy = event.clientY - gesture.current.y;

    if (gesture.current.horizontal === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      gesture.current.horizontal = Math.abs(dx) > Math.abs(dy);
      if (!gesture.current.horizontal) return;
      stopTimer();
    }

    const allowed = dx > 0 ? Boolean(start) : Boolean(end);
    travelled.current = allowed ? dx : 0;
    setOffset(travelled.current);
  };

  const finish = () => {
    stopTimer();
    const passed = Math.abs(travelled.current) >= width() * THRESHOLD;
    const action = travelled.current > 0 ? start : end;

    if (passed && action) action.onAction();
    travelled.current = 0;
    setOffset(0);
    gesture.current.horizontal = null;
  };

  const backdrop = offset > 0 ? start : offset < 0 ? end : null;
  const side = offset > 0 ? 'start' : 'end';

  return (
    <div
      ref={box}
      className="swipe"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onContextMenu={(event) => {
        // Системное меню и наше долгое нажатие — один и тот же путь, а не два подряд.
        stopTimer();
        event.preventDefault();
      }}
    >
      {backdrop ? (
        <div className={`swipe__backdrop swipe__backdrop--${side}`}>
          <Icon name={backdrop.icon} size={22} />
          <span>{backdrop.label}</span>
        </div>
      ) : null}
      <div
        className="swipe__content"
        style={{
          transform: `translateX(${offset}px)`,
          transition: offset === 0 ? 'transform 160ms ease-out' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
